import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext.jsx";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Chip,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SchoolIcon from "@mui/icons-material/School";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const PLANES = [
  {
    key: "profesionales",
    nombre: "Sistema Todo en Uno Profesionales",
    slug: "sistema-todo-en-uno-profesionales",
    precioDesde: "19.90",
    image: "https://ik.imagekit.io/wfm4vebzr/holding/services/profesionales.jpg",
    icon: <SchoolIcon sx={{ color: "#fff" }} />,
    descripcion:
      "Plataforma todo en uno para profesionales: academia, bolsa de trabajo y CRM con acceso a la comunidad.",
    beneficios: [
      "Academia comunidad",
      "Bolsa de trabajo",
      "CRM Pro",
      "Herramientas IA",
    ],
    colorPrimario: "#6C4DE2",
    colorBg: "linear-gradient(135deg, #6C4DE2 0%, #a18bfa 100%)",
  },
  {
    key: "empresa",
    nombre: "Sistema Todo en Uno Empresa",
    slug: "sistema-todo-en-uno-empresa",
    precioDesde: "29.00",
    image: "https://ik.imagekit.io/wfm4vebzr/holding/services/empresa.jpg",
    icon: <BusinessIcon sx={{ color: "#fff" }} />,
    descripcion:
      "Plataforma todo en uno para empresas: academia, bolsa de trabajo, CRM con acceso a la comunidad y cuentas de equipo.",
    beneficios: [
      "Academia comunidad",
      "Bolsa de trabajo",
      "CRM Pro",
      "Cuentas de equipo",
      "Herramientas IA",
    ],
    colorPrimario: "#0b8db5",
    colorBg: "linear-gradient(135deg, #0b8db5 0%, #4ecdc4 100%)",
  },
];

const ProPlansInfoModal = ({ open, onClose, onStartTrial, tool, plans }) => {
  const datos = plans && plans.length ? plans : PLANES;
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  const irAlServicio = (plan) => {
    const currentUserId = authUser?.id || authUser?.user_id || 1500;
    const slug = plan.slug || plan.key || "";
    let url = "";
    if (String(slug).includes("empresa")) {
      url = `/systems/bolsa/servicio-empresa/12?userId=${currentUserId}`;
    } else if (String(slug).includes("profesional") || String(slug).includes("profesionales")) {
      url = `/systems/bolsa/servicio-profesional/11?userId=${currentUserId}`;
    } else {
      url = `/systems/bolsa/servicio-profesional/11?userId=${currentUserId}`;
    }
    onClose();
    navigate(url);
  };

  const irAHerramienta = () => {
    if (tool && tool.route) {
      onClose();
      navigate(tool.route);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 130000 }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", top: 20, right: 24, zIndex: 10, color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}
        aria-label="Cerrar"
      >
        <CloseIcon />
      </IconButton>
      <Box
        sx={{
          background: "linear-gradient(135deg, #211E26 0%, #6C4DE2 60%, #F21C63 100%)",
          color: "#fff",
          py: 4,
          px: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="overline" sx={{ letterSpacing: 2.5, opacity: 0.9, fontWeight: 700 }}>
          HITPOLY PRO — SISTEMAS INTEGRADOS
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, mt: 1 }}>
          Elige tu sistema todo en uno
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.92, maxWidth: 560, mx: "auto" }}>
          Accede a la academia, bolsa de trabajo y CRM. Cada plan está diseñado para escalar tu crecimiento.
        </Typography>
      </Box>

      <DialogContent sx={{ py: 4, px: { xs: 2, md: 4 }, overflow: "auto", scrollbarWidth: "thin", scrollbarColor: "#6C4DE2 transparent", "&::-webkit-scrollbar": { width: 6 }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { backgroundColor: "#6C4DE2", borderRadius: 3 }, "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#F21C63" } }}>
        <Grid container spacing={3} justifyContent="center">
          {datos.map((plan) => (
            <Grid item xs={12} sm={6} key={plan.key || plan.slug || plan.id}>
              <Paper
                elevation={4}
                sx={{
                  p: 3.5,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 4,
                  border: "1px solid #eae6f5",
                  background: "#fff",
                  position: "relative",
                  overflow: "hidden",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 16px 40px rgba(108,77,226,0.12)",
                  },
                }}
              >
                {/* Badge de plan */}
                <Chip
                  label={plan.badge || (plan.precioDesde === "29.00" ? "EMPRESA" : "PROFESIONAL")}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    fontWeight: 800,
                    fontSize: 10,
                    letterSpacing: 0.8,
                    bgcolor: plan.colorPrimario || (plan.precioDesde === "29.00" ? "#0b8db5" : "#6C4DE2"),
                    color: "#fff",
                    borderRadius: 2,
                  }}
                />

                {/* Imagen del servicio */}
                <Box
                  component="img"
                  src={plan.image || plan.imagen || "https://ik.imagekit.io/wfm4vebzr/holding/services/default-service.jpg"}
                  alt={plan.nombre || plan.title || plan.name}
                  sx={{
                    width: "100%",
                    height: 140,
                    objectFit: "cover",
                    borderRadius: 3,
                    mb: 2.5,
                    backgroundColor: "#f0f4f8",
                  }}
                />

                {/* Cabecera con icono y título */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      background: plan.colorBg || "linear-gradient(135deg, #6C4DE2, #F21C63)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {plan.icon || (plan.precioDesde === "29.00" ? <BusinessIcon sx={{ color: "#fff" }} /> : <SchoolIcon sx={{ color: "#fff" }} />)}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#211E26", lineHeight: 1.2 }}>
                    {plan.nombre || plan.title || plan.name}
                  </Typography>
                </Box>

                {/* Precio */}
                <Box sx={{ mb: 1 }}>
                  <Typography component="span" sx={{ fontSize: "2rem", fontWeight: 900, color: plan.colorPrimario || "#6C4DE2" }}>
                    ${plan.precioDesde || plan.precio || ""}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: "0.85rem", color: "#777", ml: 0.5 }}>
                    USD / mes
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                  Desde (ver precios finales en la página del plan)
                </Typography>

                {/* Descripción */}
                <Typography variant="body2" sx={{ color: "#555", mb: 2, lineHeight: 1.55 }}>
                  {plan.descripcion || plan.description || ""}
                </Typography>

                {/* Beneficios */}
                <Box component="ul" sx={{ pl: 2, mt: 0, mb: 2.5, flexGrow: 1 }}>
                  {(plan.beneficios || plan.benefits || ["Academia comunidad", "Bolsa de trabajo", "CRM"]).map((b, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 18, color: plan.colorPrimario || "#6C4DE2", flexShrink: 0, mt: 0.2 }} />
                      <Typography variant="body2" sx={{ color: "#444", lineHeight: 1.4 }}>
                        {b}
                      </Typography>
                    </li>
                  ))}
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: "auto",
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: 3,
                    py: 1.4,
                    bgcolor: plan.colorPrimario || "#6C4DE2",
                    color: "#fff",
                    boxShadow: "0 4px 14px rgba(108,77,226,0.3)",
                    "&:hover": {
                      bgcolor: plan.colorPrimario ? plan.colorPrimario : "#5a3dcf",
                      boxShadow: "0 6px 20px rgba(108,77,226,0.35)",
                    },
                  }}
                  onClick={() => irAlServicio(plan)}
                >
                  Ver más información y precios
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3, px: 3, flexWrap: "wrap" }}>
        {tool && tool.name && (
          <Button
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: 2, px: 3, fontWeight: 700, borderColor: "#ddd", color: "#444", "&:hover": { bgcolor: "#f8f7ff", borderColor: "#6C4DE2" } }}
            onClick={irAHerramienta}
          >
            Ir a {tool.name}
          </Button>
        )}
        <Button
          variant="contained"
          sx={{ textTransform: "none", borderRadius: 2, px: 4, fontWeight: 700, bgcolor: "#F21C63", color: "#fff", boxShadow: "0 4px 14px rgba(242,28,99,0.25)", "&:hover": { bgcolor: "#d81752", boxShadow: "0 6px 20px rgba(242,28,99,0.35)" } }}
          onClick={() => {
            onClose();
            onStartTrial && onStartTrial();
          }}
        >
          O inicia 3 días gratis
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProPlansInfoModal;
